import useFormatter from "@/hooks/use-formatter.hook.ts";
import { PreviewEmployeesModal } from "@/pages/employees/employee/widgets/PreviewEmployeesModal.tsx";
import {
  useTimeOffCategoryDetails,
  useTimeOffPoliciesSort,
  useTimeOffPoliciesSortPreview,
} from "@/queries/use-time-off.query.ts";
import {
  ApiTimeOffCategoryPolicy,
  ApiTimeOffPolicyTransition,
} from "@/types/time-off/time-off-policy.types.ts";
import { DragDropContext, Draggable, Droppable, DropResult } from "@hello-pangea/dnd";
import {
  Button,
  FontAwesomeIcon,
  ListItem,
  Modal,
  ModalProps,
  Skeleton,
  Stack,
  Text,
  useModals,
} from "@skailer-com/uikit";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export interface TimeOffPoliciesSortModalProps extends ModalProps {
  categoryId: number;
}

export function TimeOffPoliciesSortModal({ categoryId, ...props }: TimeOffPoliciesSortModalProps) {
  const { data: category, isPending } = useTimeOffCategoryDetails(categoryId);
  const [sortedPolicies, setSortedPolicies] = useState<ApiTimeOffCategoryPolicy[]>(
    category?.policies ?? []
  );
  const [syncedCategory, setSyncedCategory] = useState(category);
  const [transitions, setTransitions] = useState<ApiTimeOffPolicyTransition[]>();
  const policiesSortPreview = useTimeOffPoliciesSortPreview(categoryId);
  const policiesSort = useTimeOffPoliciesSort(categoryId);
  const { t } = useTranslation();
  const { renderDate } = useFormatter();
  const { openModal, close } = useModals();
  const { id } = props;

  if (category !== syncedCategory) {
    setSyncedCategory(category);
    if (category) setSortedPolicies(category.policies);
  }

  const sortedIds = sortedPolicies.map((policy) => policy.id);

  const reviewOrder = () => {
    policiesSortPreview.mutate(sortedIds, { onSuccess: setTransitions });
  };

  const saveOrder = () => {
    policiesSort.mutate(sortedIds, { onSuccess: () => close(id) });
  };

  const handleDragEnd = ({ source, destination }: DropResult) => {
    if (!destination || destination.index === source.index) return;

    const items = Array.from(sortedPolicies);
    const [moved] = items.splice(source.index, 1);

    items.splice(destination.index, 0, moved);
    setSortedPolicies(items);
  };

  const getPolicyTitle = (policyId: number | null) =>
    sortedPolicies.find((policy) => policy.id === policyId)?.title ?? t("time_off:no_policy");

  const openEmployees = (transition: ApiTimeOffPolicyTransition) => {
    openModal(PreviewEmployeesModal, {
      filter: { page_number: 1, employee_ids: transition.employee_ids },
      addColumns: [],
      modalTitle: t("core:employees"),
    });
  };

  if (transitions) {
    return (
      <Modal
        title={t("time_off:policies_order_confirm_title")}
        {...props}
        withCloser
        actions={
          <Stack gap={"smd"} direction={"vertical"}>
            <Button
              size={"lg"}
              className={"w-full"}
              onClick={saveOrder}
              isLoading={policiesSort.isPending}
            >
              {t("common:save")}
            </Button>
            <Button
              size={"md"}
              variant={"plain"}
              className={"w-full"}
              onClick={() => setTransitions(undefined)}
            >
              {t("common:back")}
            </Button>
          </Stack>
        }
      >
        {transitions.length === 0 ? (
          <Text className={"text-secondary"}>{t("time_off:policies_order_no_transitions")}</Text>
        ) : (
          <Stack direction={"vertical"}>
            {transitions.map((transition) => (
              <ListItem
                key={`${transition.from_policy_id}-${transition.to_policy_id}`}
                title={t("time_off:policy_transition_count", {
                  count: transition.employee_ids.length,
                  from: getPolicyTitle(transition.from_policy_id),
                  to: getPolicyTitle(transition.to_policy_id),
                })}
                caption={t("time_off:changes_will_take_effect_on", {
                  date: renderDate(transition.date_effective_from),
                })}
                valueSlot={<FontAwesomeIcon icon={"fa-light fa-chevron-right"} />}
                onClick={() => openEmployees(transition)}
              />
            ))}
          </Stack>
        )}
      </Modal>
    );
  }

  return (
    <Modal
      title={t("time_off:policies_priority_title")}
      caption={t("time_off:policies_priority_caption")}
      {...props}
      withCloser
      actions={
        <Stack>
          <Button size={"lg"} onClick={reviewOrder} isLoading={policiesSortPreview.isPending}>
            {t("time_off:policies_order_review")}
          </Button>
        </Stack>
      }
    >
      {isPending ? (
        <Stack direction={"vertical"} gap={"md"}>
          {Array.from({ length: 5 }).map((_, index) => (
            <Stack key={index} direction={"horizontal"} gap={"md"} items={"center"}>
              <Skeleton className={"h-4 w-48"} />
            </Stack>
          ))}
        </Stack>
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId={"time-off-policies-list"} direction={"vertical"}>
            {(provided) => (
              <div
                className={"w-full flex flex-col overflow-y-auto"}
                ref={provided.innerRef}
                {...provided.droppableProps}
              >
                {sortedPolicies.map((policy, index) => (
                  <Draggable
                    key={policy.id}
                    draggableId={policy.id.toString()}
                    index={index}
                    isDragDisabled={policiesSortPreview.isPending}
                  >
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        style={provided.draggableProps.style}
                        className={"flex items-center gap-4 bg-light py-1 min-h-16"}
                      >
                        <div
                          className={"flex items-center justify-center cursor-grab text-secondary"}
                        >
                          <FontAwesomeIcon icon="fa-light fa-grip-dots-vertical" />
                        </div>
                        <Text>{`${index + 1}. ${policy.title}`}</Text>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}
    </Modal>
  );
}
